import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { useCurrency } from '../../contexts/CurrencyContext';

const SUB_COLORS = [
  '#93C5FD', '#9CA3AF', '#F3F4F6', '#A78BFA', '#34D399',
  '#60A5FA', '#6B7280', '#E5E7EB', '#8B5CF6', '#10B981'
];

const CustomTooltip = ({ active, payload }) => {
  const { formatAmount } = useCurrency();

  if (!active || !payload || !payload.length || !payload[0].payload) return null;

  const data = payload[0].payload;
  const parentValue = data.parentValue || 0;
  const calculatedAllocation = parentValue > 0
    ? ((data.value || 0) / parentValue * 100)
    : data.currentAllocation || 0;

  return (
    <div className="bg-white p-3 border rounded shadow">
      <p className="font-bold">{data.name}</p>
      <p>Value: {formatAmount(data.value || 0)}</p>
      <p>Allocation: {calculatedAllocation.toFixed(2)}%</p>
    </div>
  );
};

const PortfolioChart = ({ data, currentPath, onPathChange }) => {
  const { convertAmount, selectedCurrency } = useCurrency();

  const calculateTotalValue = (items) => {
    return items.reduce((sum, item) => {
      if (item.subCategories) {
        const subCategoryTotal = item.subCategories.reduce((subSum, subCategory) => {
          return subSum + calculateTotalValue(subCategory.items || []);
        }, 0);
        const itemsTotal = calculateTotalValue(item.items || []);
        return sum + subCategoryTotal + itemsTotal;
      }

      if (item.items) {
        return sum + calculateTotalValue(item.items);
      }
      
      const currency = item.symbol?.endsWith('CURRENCY') 
        ? item.symbol.split('.')[0] 
        : (item.currency || 'USD');
      
      let itemValue;
      if (item.symbol?.endsWith('CURRENCY')) {
        const position = parseFloat(item.position) || parseFloat(item.quantity) || 0;
        itemValue = convertAmount(position, currency, selectedCurrency);
      } else {
        itemValue = convertAmount(item.value || 0, currency, selectedCurrency);
      }
      
      return sum + itemValue;
    }, 0);
  };

  const processChartData = (items) => {
    return items.map(item => {
      const convertedValue = calculateTotalValue([item]);
      return {
        ...item,
        value: convertedValue
      };
    });
  };

  if (!data?.length) return null;

  const totalValue = calculateTotalValue(data);

  const chartData = processChartData(data).map(item => ({
    ...item,
    value: item.value,
    parentValue: totalValue,
    currentAllocation: totalValue > 0 ? (item.value / totalValue * 100) : 0
  }));

  const pathParts = currentPath?.split('/').filter(Boolean) || [];
  const handlePathClick = (index) => {
    const newPath = pathParts.slice(0, index + 1).join('/');
    onPathChange(newPath);
  };

  const getChartColor = (item, index) => SUB_COLORS[index % SUB_COLORS.length];

  const handleClick = (data) => {
    if (!data || !data.payload) return;
    const selectedData = data.payload;
    const newPath = currentPath
      ? `${currentPath}/${selectedData.name}`
      : selectedData.name;

    if (selectedData.subCategories?.length > 0 || selectedData.items?.length > 0) {
      onPathChange(newPath);
    }
  };

  return (
    <div className="h-96">
      <div className="mb-4 flex items-center gap-2 text-sm">
        <span
          className="text-blue-500 cursor-pointer hover:text-blue-700"
          onClick={() => onPathChange('')}
        >
          Portfolio
        </span>
        {pathParts.map((part, index) => (
          <React.Fragment key={index}>
            <span className="text-gray-400">/</span>
            <span
              className="text-blue-500 cursor-pointer hover:text-blue-700"
              onClick={() => handlePathClick(index)}
            >
              {part}
            </span>
          </React.Fragment>
        ))}
      </div>

      <div className="relative -mt-3">
        <ResponsiveContainer width="100%" height={350}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              startAngle={90}
              endAngle={-270}
              innerRadius="60%"
              outerRadius="100%"
              dataKey="value"
              onClick={handleClick}
              isAnimationActive={false}
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={getChartColor(entry, index)}
                  cursor={entry.subCategories?.length > 0 || entry.items?.length > 0 ? "pointer" : "default"}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default PortfolioChart;